import { createApp, createSSRApp, h } from 'vue'
import { createInertiaApp, router as inertiaRouter } from '@inertiajs/vue3'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import * as Sentry from '@sentry/vue'
import en from '../locales/en.json'
import th from '../locales/th.json'
import ru from '../locales/ru.json'
import zh from '../locales/zh.json'
import '../css/application.css'

// A tab opened before a deploy still holds the OLD manifest: its next
// Inertia navigation asks for a page chunk (or that chunk's CSS) whose hashed
// filename no longer exists, and Vite throws vite:preloadError ("Unable to
// preload CSS for /vite/assets/CheckEmail-….css", Sentry Sept 2026). The
// right answer is a full reload, which fetches the new bundle — once per
// URL, so a genuinely missing chunk can't turn into a reload loop.
window.addEventListener('vite:preloadError', (event) => {
  let already = false
  try {
    const key = `vite-preload-reloaded:${location.href}`
    already = sessionStorage.getItem(key) === '1'
    if (!already) sessionStorage.setItem(key, '1')
  } catch { /* storage blocked: still reload once */ }
  if (already) return
  event.preventDefault()
  window.location.reload()
})

// inertia_rails renders page data as div[data-page]; Inertia v3 JS expects a
// <script type="application/json"> element. Bridge the gap by passing the
// parsed page data directly so getInitialPageFromDOM is never called.
// Two shapes of page payload: the classic inertia_rails <div id="app" data-page>
// (client render), or — when the SSR sidecar answered — a
// <script data-page type="application/json"> next to a server-rendered
// <div id="app" data-server-rendered>. Inertia v3 reads the script itself, so
// only the div form needs the manual read.
const appEl = document.getElementById('app')
const initialPage = appEl?.dataset.page ? JSON.parse(appEl.dataset.page) : undefined
const serverRendered = !!appEl?.hasAttribute('data-server-rendered')
const defaultLang = initialPage?.props?.locale || initialPage?.props?.app_setting?.language || 'en'

// Russian plural selection for "one | few | many" 3-form messages.
// vue-i18n's default rule reads 3 forms as zero|one|many, which picks the
// wrong Russian form for every n except teens — register the real rule.
// (n % 10 === 1 except 11 → one; 2-4 except 12-14 → few; everything else,
// including 0 and teens, → many. 2-form messages keep one|other behavior.)
function ruPlural(choice, choicesLength) {
  const mod10 = choice % 10
  const mod100 = choice % 100
  const one = mod10 === 1 && mod100 !== 11
  if (choicesLength === 2) return one ? 0 : 1
  const few = mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
  return one ? 0 : few ? 1 : 2
}

const i18n = createI18n({
  legacy: false,
  locale: defaultLang,
  fallbackLocale: 'en',
  pluralRules: { ru: ruPlural },
  messages: { en, th, ru, zh },
})

// Sync vue-i18n's active locale with whatever the server says on every
// Inertia navigation. Without this, the EN/RU switch on the supplier
// portal (and the operator's Settings language toggle on next-visit)
// updates props but vue-i18n stays pinned to the page's initial locale
// because nothing else listens to the page-props change.
inertiaRouter.on('success', (event) => {
  const props = event.detail?.page?.props || {}
  const nextLocale = props.locale || props.app_setting?.language || 'en'
  if (i18n.global.locale.value !== nextLocale) {
    i18n.global.locale.value = nextLocale
  }
})

// Lazy page resolution: each Inertia page becomes its own on-demand chunk
// instead of being bundled into the main entrypoint (eager: true bundled all
// ~150 pages into one 1.6 MB chunk). The initial page chunk is awaited by
// createInertiaApp before mount; the #boot-progress bar covers that fetch, and
// SEO meta/JSON-LD are server-rendered in the layout so first paint is unaffected.
const pages = import.meta.glob('../Pages/**/*.vue')

createInertiaApp({
  page: initialPage,
  resolve: async (name) => {
    const importPage = pages[`../Pages/${name}.vue`]
    if (!importPage) throw new Error(`Inertia page not found: ${name}`)
    const page = await importPage()
    return page.default ?? page
  },
  setup({ el, App, props, plugin }) {
    const pinia = createPinia()
    // createSSRApp hydrates the server markup instead of re-rendering it;
    // on a client-rendered page it would only log hydration mismatches.
    const app = (serverRendered ? createSSRApp : createApp)({ render: () => h(App, props) })
    // No-op when VITE_SENTRY_DSN is unset (the import remains a tiny shim).
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE,
        release: import.meta.env.VITE_GIT_SHA || undefined,
        tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0),
        sendDefaultPii: false,
        // Filter third-party noise we can't fix: Telegram WebApp SDK
        // throws "Error invoking postEvent: Method not found" when the
        // public catalog opens inside Telegram's in-app browser on an
        // older client missing a given method. Not actionable on our side.
        ignoreErrors: [
          /Error invoking postEvent: Method not found/,
          /ResizeObserver loop limit exceeded/,
          /ResizeObserver loop completed with undelivered notifications/,
          // Inertia's own wrapper for a request that never reached us (the
          // visitor's connection dropped mid-submit). Nothing server-side to
          // fix, and the form shows its own retry state.
          /HttpNetworkError/,
          /^Network error \(https?:\/\//,
          // The stale-chunk case is handled by the vite:preloadError reload
          // above; the event itself is not a bug to track.
          /Unable to preload CSS for/
        ]
      })
    }
    app
      .use(plugin)
      .use(pinia)
      .use(i18n)
      .mount(el)

    // Finish the boot-progress bar once Vue is mounted.
    const boot = document.getElementById('boot-progress')
    if (boot) {
      boot.classList.add('done')
      setTimeout(() => boot.remove(), 300)
    }
  },
  // Color is overridden by #nprogress .bar { background: var(--accent) !important }
  // in application.css so it always matches the active palette.
  progress: { color: 'var(--accent)' },
})
